import React from "react";
import { Columns3Cog, GripVertical, RotateCcw } from "lucide-react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Button } from "../../../ui/button";
import { Checkbox } from "../../../ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "../../../ui/popover";
import { cn } from "../../../lib/utils";

const SortableColumnRow = ({ column, onToggle }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: column.label,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "flex items-center gap-2 rounded-sm px-1 py-1.5 text-sm",
        isDragging && "bg-accent"
      )}
    >
      <button
        type="button"
        className="cursor-grab touch-none text-muted-foreground hover:text-foreground active:cursor-grabbing"
        {...attributes}
        {...listeners}
        aria-label={`Reorder ${column.label}`}
      >
        <GripVertical className="h-4 w-4" />
      </button>
      <Checkbox
        id={`col-${column.label}`}
        checked={column.visible}
        onCheckedChange={() => onToggle(column.label)}
      />
      <label
        htmlFor={`col-${column.label}`}
        className="flex-1 cursor-pointer select-none truncate"
      >
        {column.label}
      </label>
    </div>
  );
};

// Generic per-table "manage columns" control: show/hide via checkbox,
// reorder via drag-and-drop. Fully controlled - all persisted state lives
// in useColumnPreferences; this component only renders `columns` and
// reports intent back through onToggle/onReorder/onReset.
const ColumnManager = ({ columns, onToggle, onReorder, onReset }) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  if (!columns || columns.length === 0) return null;

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = columns.findIndex((c) => c.label === active.id);
    const newIndex = columns.findIndex((c) => c.label === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    onReorder(arrayMove(columns, oldIndex, newIndex).map((c) => c.label));
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="default" size="icon" className="!h-9 !w-9" title="Manage columns">
          <Columns3Cog className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64 p-2">
        <div className="flex items-center justify-between px-1 pb-2">
          <span className="text-sm font-semibold">Columns</span>
          <Button
            variant="ghost"
            size="sm"
            className="!h-7 gap-1 px-2 text-xs"
            onClick={onReset}
          >
            <RotateCcw className="h-3 w-3" />
            Reset
          </Button>
        </div>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext
            items={columns.map((c) => c.label)}
            strategy={verticalListSortingStrategy}
          >
            <div className="max-h-80 space-y-0.5 overflow-y-auto">
              {columns.map((column) => (
                <SortableColumnRow key={column.label} column={column} onToggle={onToggle} />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      </PopoverContent>
    </Popover>
  );
};

export default ColumnManager;
